import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  OwnSaveTokens,
  type AddToCallSheetResultDto,
  type CallSheetDetailDto,
  type CallSheetMembershipDto,
  type CallSheetProgressDto,
  type CallSheetSort,
  type CallSheetDto,
  type CreateCallSheetRequest,
  type OrderCallSheetRequest,
  type SortDirection,
  type RemoveFromCallSheetResultDto,
  type RenameCallSheetRequest,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { API_BASE_URL } from '../../config';
import { apiJson, apiVoid, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { byName, invalidateAroundBooks, patchBookSheets, refetchBooksWhere, showsCallBook } from './prospectBookCache';

const CALL_SHEETS_PATH = '/api/fundraising/call-sheets';
const CALL_SHEETS_URL = `${API_BASE_URL}${CALL_SHEETS_PATH}`;
// The Caller's read of every sheet with its progress — the same counts the phone's picker shows.
const SHEETS_PROGRESS_URL = `${API_BASE_URL}/api/fundraising/call-time/sheets`;

/**
 * Every call book with its progress (people, called from it, call-backs due), by name — the Call books tab.
 * One request, the same reading the Caller's phone shows.
 */
export function useCallSheetsProgress() {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    queryKey: queryKeys.fundraising.callSheets(),
    queryFn: async () => {
      const response = await send(SHEETS_PROGRESS_URL, {}, 'Failed to fetch the call books');
      return response.json() as Promise<CallSheetProgressDto[]>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

/** An order a Fundraiser is only looking at a call book in — never saved, never what Callers get. */
export interface CallSheetView {
  sort?: CallSheetSort | null;
  dir?: SortDirection | null;
}

/**
 * One call book and its people, each with the Ask frozen when they were added: in its saved order (the one
 * Callers work it in), or in `view` — sorted by the server for this read only. While another view loads,
 * the last one of the same book stays on screen (`isPlaceholderData`).
 */
export function useCallSheet(id: string, view: CallSheetView = {}) {
  const { selectedCampaign } = useCampaignStore();
  const sort = view.sort ?? null;
  const dir = view.dir ?? null;
  return useQuery({
    queryKey: queryKeys.fundraising.callSheet(id, sort, dir),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (sort) params.set('sort', sort);
      if (dir) params.set('dir', dir);
      const query = params.toString();
      const response = await send(`${CALL_SHEETS_URL}/${id}${query ? `?${query}` : ''}`, {}, 'Failed to fetch the call book');
      return response.json() as Promise<CallSheetDetailDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    // Only this book's last view — never another book's rows under this one's name.
    placeholderData: (previous) => (previous?.sheet.id === id ? previous : undefined),
  });
}

/** A request the server refused, with its status. */
class CallSheetRequestError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/** Whether a change was refused because someone else changed the book first (a stale token, `409`). */
export const isCallSheetChangedElsewhere = (error: unknown): boolean =>
  error instanceof CallSheetRequestError && error.status === 409;

/**
 * A sheet as a change takes it — which one and the token it was read with — from a book's membership or the
 * summary's list alike (who's on it is never needed).
 */
type SheetRef = Pick<CallSheetMembershipDto, 'id' | 'name' | 'size' | 'concurrencyVersion'> & { prospectIds?: string[] };

// Sheet changes run one at a time: several sheets can be ticked from one menu in quick succession.
const SCOPE = { id: 'fundraising-call-sheet' };

// The reads still pass an empty init (`authenticatedFetch(url, {})`), which the shared helper never sends.
async function send(url: string, init: RequestInit, fallback: string): Promise<Response> {
  const response = await authService.authenticatedFetch(url, init);
  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new CallSheetRequestError(error?.error ?? error?.message ?? fallback, response.status);
  }
  return response;
}

/** A non-ok response as the typed error: the server's sentence (`error`, else `message`; a JSON `null` body reaches here as null), else `fallback`. */
const requestError = (fallback: string) => (body: ApiErrorBody | null, response: Response) =>
  new CallSheetRequestError(body?.error ?? body?.message ?? fallback, response.status);

function sendJson<T>(method: 'POST' | 'PUT', path: string, body: unknown, fallback: string): Promise<T> {
  return apiJson<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    json: body,
    error: requestError(fallback),
  });
}

// One record of this tab's own sheet saves, shared by every sheet change — adding, removing, renaming and
// deleting all step the same token — and scoped to the query client, so a fresh client starts clean.
const ownSavesByClient = new WeakMap<QueryClient, OwnSaveTokens>();
function useOwnSheetSaves() {
  const queryClient = useQueryClient();
  let ownSaves = ownSavesByClient.get(queryClient);
  if (!ownSaves) ownSavesByClient.set(queryClient, (ownSaves = new OwnSaveTokens()));
  return ownSaves;
}

/**
 * Write a sheet's fresh name, size, token and people into every cached book (or drop a deleted one) from what the
 * change returned — never re-reading the book, which is the whole campaign. Only a book narrowed to the changed
 * sheet, or sorted by how many books people are on, is read again: what it shows and in what order is the
 * server's to say. Not optimistic: an add skips people only the server can judge.
 */
function useSheetCache() {
  const queryClient = useQueryClient();
  const write = (sheet: CallSheetDto, people: (ids: string[]) => string[] = (ids) => ids) =>
    patchBookSheets(queryClient, (sheets) =>
      byName(
        sheets.map((s) =>
          s.id === sheet.id
            ? { ...s, name: sheet.name, size: sheet.size, concurrencyVersion: sheet.concurrencyVersion, prospectIds: people(s.prospectIds) }
            : s,
        ),
      ),
    );
  return {
    write,
    add: (sheet: CallSheetDto, added: string[]) =>
      write(sheet, (ids) => [...ids, ...added.filter((id) => !ids.includes(id))]),
    remove: (sheet: CallSheetDto, removed: string[]) => write(sheet, (ids) => ids.filter((id) => !removed.includes(id))),
    create: (sheet: CallSheetDto, added: string[]) =>
      patchBookSheets(queryClient, (sheets) =>
        byName([
          ...sheets.filter((s) => s.id !== sheet.id),
          { id: sheet.id, name: sheet.name, size: sheet.size, concurrencyVersion: sheet.concurrencyVersion, prospectIds: added },
        ]),
      ),
    drop: (sheetId: string) => patchBookSheets(queryClient, (sheets) => sheets.filter((s) => s.id !== sheetId)),
    /**
     * The small reads around a sheet change: the Call books tab's progress list and one book's rows, a person's
     * page (their books and timeline) and the summary (the tab's count and the sheets' names and tokens) — and,
     * when the change moved `sheetId`'s people, the books that show them.
     */
    refetch: (sheetId?: string) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.callSheets() }),
        invalidateAroundBooks(queryClient),
        sheetId ? refetchBooksWhere(queryClient, showsCallBook(sheetId)) : undefined,
      ]),
  };
}

/** Create a call sheet with people on it (Fundraising: Manage). A refusal rejects with the server's message. */
export function useCreateCallSheet() {
  const cache = useSheetCache();
  return useMutation({
    scope: SCOPE,
    mutationFn: (request: CreateCallSheetRequest) =>
      sendJson<AddToCallSheetResultDto>('POST', CALL_SHEETS_PATH, request, "Couldn't create the call book. Try again."),
    onSuccess: (result) => cache.create(result.sheet, result.addedProspectIds),
    onSettled: (result) => cache.refetch(result?.sheet.id),
  });
}

/**
 * Add people to a sheet or take them off (Fundraising: Manage), following the recipe for mutations that
 * carry a concurrency token (apps/web/CLAUDE.md): the server's fresh token is written back into the cached
 * book, and each change sends the token its menu read, stepped forward only through this tab's own changes —
 * of any kind, add, remove, rename or delete — so a quick second click doesn't false-conflict while someone
 * else's change still makes it a 409.
 */
function useChangeCallSheetPeople<T extends { sheet: CallSheetDto }>(
  path: string,
  fallback: string,
  patch: (cache: ReturnType<typeof useSheetCache>, result: T, prospectIds: string[]) => void,
) {
  const cache = useSheetCache();
  const ownSaves = useOwnSheetSaves();
  return useMutation({
    scope: SCOPE,
    mutationFn: async ({ sheet, prospectIds }: { sheet: SheetRef; prospectIds: string[] }) => {
      const sent = ownSaves.tokenFor(sheet.id, sheet.concurrencyVersion);
      const result = await sendJson<T>('POST', `${CALL_SHEETS_PATH}/${sheet.id}/${path}`, { prospectIds, concurrencyVersion: sent }, fallback);
      ownSaves.record(sheet.id, sent, result.sheet.concurrencyVersion);
      return result;
    },
    onSuccess: (result, { prospectIds }) => patch(cache, result, prospectIds),
    onSettled: (_result, _error, { sheet }) => cache.refetch(sheet.id),
  });
}

/** Add people to a sheet; the result says who was added, skipped and why, and who's on another sheet. */
export function useAddToCallSheet() {
  return useChangeCallSheetPeople<AddToCallSheetResultDto>(
    'prospects',
    "Couldn't add them to the call book. Try again.",
    (cache, result) => cache.add(result.sheet, result.addedProspectIds),
  );
}

/** Take people off a sheet. Their calls stay on their own record. */
export function useRemoveFromCallSheet() {
  return useChangeCallSheetPeople<RemoveFromCallSheetResultDto>(
    'prospects/remove',
    "Couldn't take them off the call book. Try again.",
    // Anyone named who wasn't on it is simply not there.
    (cache, result, prospectIds) => cache.remove(result.sheet, prospectIds),
  );
}

/** Rename a sheet (Fundraising: Manage); its people stay. A refusal — a taken name, a stale token — rejects with the server's message. */
export function useRenameCallSheet() {
  const cache = useSheetCache();
  const ownSaves = useOwnSheetSaves();
  return useMutation({
    scope: SCOPE,
    mutationFn: async ({ sheet, name }: { sheet: SheetRef; name: string }) => {
      const sent = ownSaves.tokenFor(sheet.id, sheet.concurrencyVersion);
      const request: RenameCallSheetRequest = { name, concurrencyVersion: sent };
      const result = await sendJson<CallSheetDto>('PUT', `${CALL_SHEETS_PATH}/${sheet.id}`, request, "Couldn't rename the call book. Try again.");
      ownSaves.record(sheet.id, sent, result.concurrencyVersion);
      return result;
    },
    onSuccess: (sheet) => cache.write(sheet),
    onSettled: () => cache.refetch(),
  });
}

/**
 * Set a sheet's order (Fundraising: Manage) — the order Callers work it in on the phone, and every surface
 * shows it in. The server sorts; the call book is re-read in its new order before this settles, so
 * `isPending` covers the whole change. A null column puts it back in its default order, largest Ask first.
 */
export function useOrderCallSheet() {
  const queryClient = useQueryClient();
  const cache = useSheetCache();
  const ownSaves = useOwnSheetSaves();
  return useMutation({
    scope: SCOPE,
    mutationFn: async ({ sheet, sortBy, sortDescending }: { sheet: SheetRef; sortBy: CallSheetSort | null; sortDescending: boolean }) => {
      const sent = ownSaves.tokenFor(sheet.id, sheet.concurrencyVersion);
      const request: OrderCallSheetRequest = { sortBy, sortDescending, concurrencyVersion: sent };
      const result = await sendJson<CallSheetDto>('PUT', `${CALL_SHEETS_PATH}/${sheet.id}/order`, request, "Couldn't change the call book's order. Try again.");
      ownSaves.record(sheet.id, sent, result.concurrencyVersion);
      return result;
    },
    onSuccess: (sheet) => cache.write(sheet),
    // Settled once the call books are re-read in their new order. A book narrowed to this one is read again in
    // it, behind the headers; no other book shows a sheet's order.
    onSettled: (_sheet, _error, { sheet }) => {
      void invalidateAroundBooks(queryClient);
      // Only when it's in the call book's own order: a column sort doesn't show it.
      void refetchBooksWhere(queryClient, (query) => query.sheetId === sheet.id && !query.sort && !query.dir);
      return queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.callSheets() });
    },
  });
}

/** Delete a sheet and its rows (Fundraising: Manage). Nobody leaves the book, and every call stays on its person. */
export function useDeleteCallSheet() {
  const cache = useSheetCache();
  const ownSaves = useOwnSheetSaves();
  return useMutation({
    scope: SCOPE,
    mutationFn: async ({ sheet }: { sheet: SheetRef }) => {
      await apiVoid(`${CALL_SHEETS_PATH}/${sheet.id}?concurrencyVersion=${ownSaves.tokenFor(sheet.id, sheet.concurrencyVersion)}`, {
        method: 'DELETE',
        error: requestError("Couldn't delete the call book. Try again."),
      });
      return sheet.id;
    },
    onSuccess: (sheetId) => cache.drop(sheetId),
    onSettled: (_sheetId, _error, { sheet }) => cache.refetch(sheet.id),
  });
}
